import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'next/navigation';
import { nanoid } from 'nanoid';
import { IDENTIFIERS, getUrl } from '@helpers/urlsHelper';
import useNoodleApi from '@hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import Plus from '@components/Icons/Plus';
import { HookResponse, PAGE_SIZE, ResourceType } from './common';

const getDetailPagePath: HookResponse['getDetailPagePath'] = ({ id }) => getUrl(IDENTIFIERS.DASHBOARD_CREDITORS_EDIT, { id });

const useCreditors = ({
  creatorId,
  search,
  resourceType,
  perPage = PAGE_SIZE,
}: {
  creatorId: string | undefined;
  search: string | null;
  resourceType: ResourceType;
  perPage?: number;
}): HookResponse & {
  createCreditorModalOpen: boolean;
  setCreateCreditorModalOpen: (v: boolean) => void;
} => {
  const [createCreditorModalOpen, setCreateCreditorModalOpen] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [numPages, setNumPages] = useState(0);
  const pathParams = useParams<{ id?: string }>();
  const [items, setItems] = useState<HookResponse['items']>([]);
  const mostRecentRequestRef = useRef<string | null>(null);

  const { getData: getCreatorCreditorsFn } = useNoodleApi(tsClient.creators.getCreatorCreditors);

  const selectedId = pathParams?.id;

  const loadPage = useCallback(async ({ page, search: newSearch }: {search: string | null, page: number }): Promise<void> => {
    if (creatorId && resourceType === 'creditors') {
      setCurrentPage(page);
      const thisRequestId = nanoid();
      mostRecentRequestRef.current = thisRequestId;

      setIsFetching(true);
      const response = await getCreatorCreditorsFn({
        creatorId,
        page,
        perPage,
        search: newSearch ?? undefined,
      });

      if (mostRecentRequestRef.current === thisRequestId) {
        if (response.data) {
          setItems(response.data.items.map(creditor => ({
            bottomLine: `${creditor.address.line1}`,
            href: getUrl(IDENTIFIERS.DASHBOARD_CREDITORS_EDIT, { id: creditor.id }),
            id: creditor.id,
            label: creditor.name,
          })));
          setNumPages(response.data.numPages);
        }
        setCreateCreditorModalOpen(false);
        setIsFetching(false);
      }
    } else {
      setItems([]);
    }
  }, [
    creatorId,
    resourceType,
    getCreatorCreditorsFn,
    perPage,
  ]);

  useEffect(() => {
    loadPage({ page: 1, search });
  }, [loadPage, search]);

  const headerActions = [{
    icon: Plus,
    label: 'Create Creditor',
    onClick: () => setCreateCreditorModalOpen(true),
  }];

  const pagination: HookResponse['pagination'] = {
    numPages,
    onChangePage: (newPage) => {
      loadPage({ page: newPage, search });
    },
    page: currentPage,
  };

  return {
    createCreditorModalOpen,
    getDetailPagePath,
    headerActions,
    ifNoItemsText: 'No creditors yet',
    isFetchingList: isFetching,
    items,
    pagination,
    reloadList: () => loadPage({ page: 1, search }),
    selectedId,
    setCreateCreditorModalOpen,
  };
};

export default useCreditors;